import { XyzPoint } from 'cd-eft/models/SurveyEngineering/XyzPoint';
import {
  esriGeometryEngine,
  esriProjection,
  Extent,
  FeatureLayer,
  Geometry,
  Graphic,
  GraphicsLayer,
  MapView,
  Point,
  Polyline,
  Circle,
  SimpleFillSymbol,
  SimpleLineSymbol,
  SimpleMarkerSymbol,
  SketchViewModel,
  GroupLayer,
} from 'cd-eft/utils/esriTypes';
import { DynamicProfileLayerType, ProfileKeyCode, SketchState } from '../DynamicProfileEnums';
import { DynamicAlignmentHub } from './DynamicAlignmentHub';
import { DynamicMapStoreBase } from '../DynamicMapStoreBase';
import { action } from 'mobx';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';
import FeatureSnappingLayerSource from '@arcgis/core/views/interactive/snapping/FeatureSnappingLayerSource';
import { AppConstants } from 'cd-eft/appConstants';
import stores from 'cd-eft/models/Stores';
import { getElevationUsingContours } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import { doEvents } from 'cd-eft/utils/sysUtils';
import { isPointInsidePolygon, getBoundaryPolygon, isSurveyPoint } from 'cd-eft/utils/gisUtils';
import GraphicsLayerView from '@arcgis/core/views/layers/GraphicsLayerView';
import {StructureProfileController} from 'cd-eft/services/eftSetApi';
import { calcTangentCircle } from 'cd-eft/utils/planeGeometryUtils';
import LabelClass from '@arcgis/core/layers/support/LabelClass';
import { formatStationString } from 'cd-eft/utils/stationUtilities';
import {reachHighlightSymbol , trackerSymbol}from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';
import { ElevationInterpolater } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/elevationInterpolationEngine';
import { convertLinearUnit } from 'cd-eft/utils/structureLayerUtils';
import { DynamicAlignmentProfileStore } from './DynamicAlignmentProfileStore';
import { DynamicAlignmentTableStore } from './DynamicAlignmentTableStore';
import { __lineLabelSymbol } from 'cd-eft/utils/esriSymbols';
import { updateCurveAfterPointDeleted, updateCurvesHandler } from 'cd-eft/components/Design/alignment/IndependentAlignment/IndependentAlignmentVM';
import { appConfig } from 'cd-eft/config/AppConfig';
import { DisplayAlignment } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/loadLayers';

const { projectsLibraryStore, alignmentDesignStore, terraceDesignStore, designStore} = stores.AppStore;
const gisModel = stores.AppStore.surveyEngineeringStore.gisModel;

const alignmentSymbol = {
  designLineSymbol: new SimpleLineSymbol({ color: 'blue', width: 2 }),
  curveLineSymbol: new SimpleLineSymbol({ color: 'green', width: 2 }),
  invalidLineSymbol: new SimpleLineSymbol({ color: 'red', width: 5 }),
};

const AlignmentStationSymbol = {
  AlignmentStationSymbol: new SimpleMarkerSymbol({ color: 'transparent', size: 7, style: 'circle', outline: { width: 0 } }),
  editableStationSymbol: new SimpleMarkerSymbol({ color: '#FF8607', size: 7, style: 'circle', outline: { width: 0 } }),
  invisibleSymbol: new SimpleMarkerSymbol({ color: [0, 0, 0, 0], outline: undefined }),
}
const pointLabelFields: any = [
  {
    name: 'ObjectID',
    type: 'oid',
  },
  {
    name: 'Station',
    type: 'string',
  }
];

const autoLabelFields: any = [
  {
    name: 'ObjectID',
    type: 'oid',
  },
  {
    name: 'Description',
    type: 'string',
  }
];


const pointLabelClass: LabelClass = new LabelClass({
  symbol: {
    type: 'text',
    color: "black",
    font: {
      family: 'Ubuntu Mono',
      size: 11,
    },
  },
  labelPlacement: "below-right",
  labelPosition: 'curved',
  labelExpressionInfo: {
    expression: '$feature.station',
  },
  deconflictionStrategy: 'none', //'none'
});

const autoLabelClass: LabelClass = new LabelClass({
  symbol: __lineLabelSymbol,
  labelPlacement: "above-left",
  labelPosition: 'curved',
  labelExpressionInfo: {
    expression: '$feature.description',
  },
  deconflictionStrategy: 'none', //'none'
});

/**
 * Implements a custum sketch view model to complement the esri one when needed.
 * This is just a start. When it grows more mature, it can be elevated to a more common level.
 */
class RubberbandSketchViewModel {
  private _rubberbandSketchLayer: GraphicsLayer | undefined = void 0;
  private startPoint: Point | undefined = void 0;
  private mapView: MapView | undefined = void 0;
  private viewPointerMoveHandle;
  private viewImmediateClickHandle;

  constructor(mapView: MapView, sketchLayer: GraphicsLayer) {
    this.mapView = mapView;
    this._rubberbandSketchLayer = sketchLayer;
  }

  public create(startPoint?: Point) {
    this.startPoint = startPoint;
    this.viewImmediateClickHandle = this.mapView!.on('immediate-click', this.viewImmediateClickHandler);
    this.viewPointerMoveHandle = this.mapView!.on('pointer-move', this.viewPointerMoveHandler);
  }

  public complete() {
    this.viewPointerMoveHandle?.remove();
    this.viewImmediateClickHandle?.remove();
    this.rubberbandSketchLayer.removeAll();
  }

  protected get rubberbandSketchLayer() {
    return this._rubberbandSketchLayer!;
  }

  private viewPointerMoveHandler = async (event: __esri.ViewPointerMoveEvent) => {
    this.rubberbandSketchLayer.removeAll();
    const movingPoint = this.mapView?.toMap(event);

    if (this.startPoint && movingPoint) {
      const rubberBandLine = new Polyline({ spatialReference: this.mapView!.spatialReference, hasZ: true });
      rubberBandLine.addPath([this.startPoint, movingPoint]);

      const rubberBandGraphic = new Graphic({ geometry: rubberBandLine, symbol: alignmentSymbol.designLineSymbol });
      this.rubberbandSketchLayer.add(rubberBandGraphic);
    }
  };

  private viewImmediateClickHandler = async (event: __esri.ViewImmediateClickEvent) => {
    this.complete();
  };
}
const ERROR_SELF_CROSSING = 'Alignment_03';
class DynamicAlignmentMapStore extends DynamicMapStoreBase {
  protected hub: DynamicAlignmentHub;

  private isAfterReshapeVertexAdd = false;
  private alignmentObject;

  private _extendSketchLayer: GraphicsLayer | undefined = void 0;
  private extendSketchVM: SketchViewModel | undefined = void 0;

  private insertPointStatus: 'startPoint' | 'stopPoint' | 'regular' = 'regular';
  private rubberbandSketchVm: RubberbandSketchViewModel | undefined;

  private _isValidLine = true;

  private _loopThreshold = 0;

  private _isFirstVertexOutside = false;
  private mapImmediateClickHandle;
  private mapPointerMoveHandle;
  private isDeleteActive: boolean = false;
  private applyCurvesFunction;

  private __curveCircleIHandle: IHandle | undefined = void 0;
  private __currentCurveCircle: Circle | null = null;

  private _mapTrackLayer: GraphicsLayer | undefined = void 0;
  protected _trackerGraphic: Graphic | undefined = void 0;
  protected _stationGraphic: Graphic | undefined = void 0;
  protected _lookupDistance: number[] = [];
  protected _lookupXY: number[][] = [];
  protected _pointsPreviewLayerId: string = 'points_preview_layer';
  public autoAlignmentLabelLayerId: string = 'auto_alignment_label';
  private autoGenerated: boolean = false;

  private viewPointerMoveHandle1;

  constructor(hub: DynamicAlignmentHub) {
    super();

    this.hub = hub;
    this.profileKeys = [ProfileKeyCode.IA];
    // console.log('MapStore type: Alignment.');

  }

  configSketchVM() {
    if (this.mainSketchVM) {
      this.mainSketchVM.polylineSymbol = alignmentSymbol.designLineSymbol;
      this._isValidLine = true;

      this.mainSketchVM.updateOnGraphicClick = false;
      this.mainSketchVM.defaultUpdateOptions = {
        toggleToolOnClick: false, // only reshape operation will be enabled
      };
    }
  }

  public get isAlignmentValid() {
    return this._isValidLine;
  }

  // SetStore
  set setStore(setStore) {
    this.hub.setStore = setStore;
  }

  get setStore() {
    return this.hub.setStore;
  }

  @action
  protected reset(): void {
    this.sketchLayer.graphics.removeAll();
    this.curveLayer.graphics.removeAll();
    this.curveControlLayer.graphics.removeAll();
    this.userActivePoints.length = 0;
    this._isFirstVertexOutside = false;
  }

  protected get extendSketchLayer() {
    const id = 'alignment_extend_layer';
    const title = 'Alignment Extend Sketch';

    if (!this._extendSketchLayer) {
      const layer = this.mapView?.map.findLayerById(id);
      if (layer) {
        this._extendSketchLayer = layer as GraphicsLayer;
      } else {
        this._extendSketchLayer = new GraphicsLayer({ id, title, listMode: 'hide' });
        this.mapView?.map.add(this._extendSketchLayer);
      }
    }

    return this._extendSketchLayer;
  }

  moreSketchVmSetup() {
    // console.log('new Extend SketchVm for MapAlignment MapStore');
    this.extendSketchVM = new SketchViewModel({
      layer: this.extendSketchLayer,
      view: this.mapView!,
    });

    this.rubberbandSketchVm = new RubberbandSketchViewModel(this.mapView!, this.extendSketchLayer);
    this.extendSketchVM?.on('create', (event) => this.handleExtendSketchVmCreate(event));
  }

  private async handleExtendSketchVmCreate(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    if (event.state === 'complete') {
      this.extendSketchLayer.graphics.pop();

      const insertPoint = event.graphic.geometry as Point;

      const graphic = this.sketchLayer.graphics.getItemAt(0);
      const alignment = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
      let newIndex = -1;

      if (this.insertPointStatus === 'startPoint') {
        newIndex = 0;
      } else if (this.insertPointStatus === 'stopPoint') {
        newIndex = alignment.paths[0].length;
      }

      if (newIndex > -1) {
        // update alignment
        const cloneAligment = alignment.clone();
        const extendedLIne = cloneAligment.insertPoint(0, newIndex, insertPoint);
        if (this.isValidFromStart(extendedLIne)) {
          graphic.geometry = extendedLIne;

          this.clearInvalidState(graphic);

          // prepare for profile view
          const dataPoint = esriProjection.project(insertPoint as Geometry, this.dataSpatialReference!);
          const dataX = (dataPoint as Point).x;
          const dataY = (dataPoint as Point).y;
          let dataZ = designStore.currentSurface.surfaceDefClassName === 'survey' ? ElevationInterpolater.Current?.Interpolate(dataX, dataY, insertPoint) : await getElevationUsingContours(dataPoint, insertPoint, this.mapView);
          if (dataZ === undefined || dataZ === 0) {
            dataZ = isSurveyPoint(dataX, dataY);
          }

          const bPolygon = getBoundaryPolygon(projectsLibraryStore.projectSpatialReference);
          let pointInsidePolygon = await isPointInsidePolygon(dataX, dataY, bPolygon, projectsLibraryStore.projectSpatialReference!);
          if (!pointInsidePolygon) {
            const value = isSurveyPoint(dataX, dataY);
            pointInsidePolygon = value === 0 ? false : true;
          }
          if (!pointInsidePolygon) {
            MessageCenter.addWarningFromCode('Alignment_22');
            projectsLibraryStore.resetZValue = true;
          }
          const newVertex: XyzPoint = new XyzPoint(dataX, dataY, dataZ);

          this.broadcastInsertVertexInfo(newVertex, newIndex, this.profileKeys[0], true, this.alignmentObject);

          if (!projectsLibraryStore.resetZValue) {
            await this.generateAndDisplayAlignmentProfile(extendedLIne.paths[0]);
          }
          this.curveLayer.graphics.removeAll();
          if (this.applyCurvesFunction) this.applyCurvesFunction();
        } else {
          graphic.symbol = alignmentSymbol.invalidLineSymbol;
          await MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);

          // clear error state
          setTimeout(() => {
            graphic.symbol = alignmentSymbol.designLineSymbol;
            this._isValidLine = true;
          }, 500);
          setTimeout(() => {
            MessageCenter.clearMessages();
          }, 1500);
        }
      }
    }
  }

  toggleAlignmentSnapping() {
    const surveyPointsLayer = this.mapView?.map.findLayerById(AppConstants.SurveyPointsLayerId) as GraphicsLayer;
    if (this.mainSketchVM?.snappingOptions.enabled) {
      surveyPointsLayer.visible = false;
      this.mainSketchVM!.snappingOptions.enabled = false;
    } else {
      const fsls = new FeatureSnappingLayerSource({ enabled: true, layer: surveyPointsLayer } as any);
      this.mainSketchVM!.snappingOptions.featureSources.push(fsls);
      this.mainSketchVM!.snappingOptions.distance = 21;
      surveyPointsLayer.visible = true;
      this.mainSketchVM!.snappingOptions.selfEnabled = false;
      this.mainSketchVM!.snappingOptions.enabled = true;
    }
  }

  SketchPolyline() {
    this.startTrackCurveCircle();
    this.clearDeleteStation();
    this.reset();
    this.broadcastSketchState({ state: SketchState.START, keyCode: this.profileKeys[0] });
    this.mainSketchVM?.create('polyline');
    this._mapTooltipProvider?.start();
  }

  SketchPoint(applyDefaultCurves?) {
    if (applyDefaultCurves) this.applyCurvesFunction = applyDefaultCurves;
    this.clearDeleteStation();
    if (this.mainSketchVM) {
      this.mainSketchVM!.snappingOptions = {
        distance: 21,
        enabled: true,
        featureSources: [{ layer: this.sketchLayer }] as any,
      } as any;
    }

    this.mainSketchVM?.create('point');
  }

  ReshapePolyline(applyDefaultCurves?) {
    if (applyDefaultCurves) this.applyCurvesFunction = applyDefaultCurves;
    this.clearDeleteStation();
    if (this.mainSketchVM?.layer && this.mainSketchVM.layer.graphics && this.mainSketchVM.layer.graphics.length > 0) {
      this.mainSketchVM?.update([this.mainSketchVM.layer.graphics.getItemAt(0)], {
        tool: 'reshape',
        enableRotation: false,
        enableScaling: false,
        preserveAspectRatio: false,
        toggleToolOnClick: false,
      });
      this.isAfterReshapeVertexAdd = false;
    }
  }

  private AlignmentStationPointRenderer = {
    type: 'simple',
    symbol: AlignmentStationSymbol.AlignmentStationSymbol,
  };

  LoadEditToMap(data: any, keyCode: ProfileKeyCode) {
    this.alignmentObject = data;
    this.reset();
    this.setStore.isSketchMode = false;

    let alignment = this.alignmentObject.alignmentXY;
    if (!alignment) alignment = this.alignmentObject.alignmentStations;

    if (alignment) {
      const paths = [
        alignment.map((r) => {
          return [r.x, r.y, r.z];
        }),
      ];

      const dataAlignmentLine = new Polyline({
        spatialReference: this.dataSpatialReference,
        paths,
      });

      const mapAlignmentLine = esriProjection.project(dataAlignmentLine, this.mapSpatialReference!);

      const graphic = new Graphic({
        geometry: mapAlignmentLine as Polyline,
        symbol: alignmentSymbol.designLineSymbol,
      });

      this.sketchLayer.add(graphic);
    }

    const curvedAlignment = this.alignmentObject.curvedAlignmentXY;
    this.drawCurve(curvedAlignment);

    this.startTrackCurveCircle();
  }

  drawCurve(curveData: any) {
    this.curveLayer.removeAll();

    if (curveData) {
      const paths = [
        curveData.map((r) => {
          return [r.x, r.y, r.z];
        }),
      ];

      const dataAlignmentLine = new Polyline({
        spatialReference: this.dataSpatialReference,
        paths,
      });

      const graphic = new Graphic({
        geometry: dataAlignmentLine,
        symbol: alignmentSymbol.curveLineSymbol,
      });

      this.curveLayer.add(graphic);
    }
  }

  startTrackCurveCircle = () => {
    if (!this.__curveCircleIHandle) {
      this.__curveCircleIHandle = this.mapView?.on("pointer-move", ["Shift"], (e) => {
        const stations = this.hub.TableStore?.currentObject.alignmentStations;
        if (this.hub.TableStore && this.hub.TableStore.focusStationIndex > 0) {
          const forcusIndex = this.hub.TableStore.focusStationIndex;
          const station = stations[forcusIndex];
          const event = this.mapView?.toMap(e);
          const dataPoint = esriProjection.project(event as Point, this.dataSpatialReference!) as Point;

          if (!this.__currentCurveCircle || (this.__currentCurveCircle && esriGeometryEngine.contains(this.__currentCurveCircle, dataPoint))) {
            const deltaX = station.x - dataPoint.x;
            const deltaY = station.y - dataPoint.y;
            const dist = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

            const radius = Math.round(dist * 0.9);
            const circleProps = calcTangentCircle(stations, this.hub.TableStore!.focusStationIndex, radius);
            this.drawCurveCircle(circleProps.centerX, circleProps.centerY, radius)
            stations[this.hub.TableStore!.focusStationIndex].curveValue = radius;
          }
        }
      }
      )
    }

  }

  drawCurveCircle(x: number, y: number, radius = 50) {
    this.curveControlLayer.removeAll();

    const centerPt = new Point({ x, y, spatialReference: this.dataSpatialReference })

    const centerGraphic = new Graphic({
      geometry: centerPt,
      symbol: new SimpleMarkerSymbol({
        style: "x",
        color: "red",
        size: "5px",
      })
    });

    const circleGeometry = new Circle({
      center: centerPt,
      radius: radius,
      radiusUnit: "feet",
      spatialReference: this.dataSpatialReference,
    });

    this.__currentCurveCircle = circleGeometry;

    const circleGraphic = new Graphic({
      geometry: circleGeometry,
      symbol: new SimpleFillSymbol({
        style: "none",
        outline: {
          width: 1,
          color: "red"
        }
      })
    });


    this.curveControlLayer.removeAll();
    this.curveControlLayer.add(circleGraphic);
    this.curveControlLayer.add(centerGraphic);
  }

  clearCurveCircle() {
    this.curveControlLayer.removeAll()
  }

  handlePolylineCreateEvent = async (event: __esri.SketchViewModelCreateEvent): Promise<void> => {
    this.autoGenerated = false;
    if (event.state === 'start' || event.state === 'active') {
      if (event.toolEventInfo.type === 'vertex-add') {
        designStore.disableInput = true;
        const [x, y] = event.toolEventInfo.vertices[0].coordinates;
        if (event.state === 'start') {
          this._isValidLine = true;
          this._checkMapViewExtent(x, y);
        }

        if (this._isValidLine) {
          const line = event.graphic.geometry as Polyline;
          if (this.isValidLastSegment(line)) {
            this.clearInvalidState(event.graphic);

            // const [x, y] = event.toolEventInfo.vertices[0].coordinates;
            const vertexIndex = event.toolEventInfo.vertices[0].vertexIndex;

            const mapPoint = new Point({ x: x, y: y, spatialReference: this.mapSpatialReference });
            const dataPoint = esriProjection.project(mapPoint as Geometry, this.dataSpatialReference!);

            // prepare for profile view
            const dataX = (dataPoint as Point).x;
            const dataY = (dataPoint as Point).y;


            let dataZ = designStore.currentSurface.surfaceDefClassName === 'survey' ? ElevationInterpolater.Current?.Interpolate(dataX, dataY, mapPoint) : await getElevationUsingContours(dataPoint, mapPoint, this.mapView);

            if (dataZ === undefined || dataZ === 0) {
              dataZ = isSurveyPoint(dataX, dataY);
            }

            const is3dAlignment = await this.checkFor3dAlignment(vertexIndex, false);
            const bPolygon = getBoundaryPolygon(projectsLibraryStore.projectSpatialReference);
            let pointInsidePolygon = await isPointInsidePolygon(dataX, dataY, bPolygon, projectsLibraryStore.projectSpatialReference!);
            if (!pointInsidePolygon) {
              const value = isSurveyPoint(dataX, dataY);
              pointInsidePolygon = value === 0 ? false : true;
            }
            if (!pointInsidePolygon || !is3dAlignment) {
              MessageCenter.addWarningFromCode('Alignment_22');
              projectsLibraryStore.resetZValue = true;
            }

            const newVertex: XyzPoint = new XyzPoint(dataX, dataY, dataZ);
            if (this._mapTooltipProvider) this._mapTooltipProvider.currentVertex = newVertex;

            // broadcast for table
            this.broadcastNewVertexInfo(newVertex, event.state, vertexIndex, dataZ, dataX, dataY, this.profileKeys[0], false, true, true);

          } else {
            event.graphic.symbol = alignmentSymbol.invalidLineSymbol;
            await MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);
          }
        } else {
          // This is for the case of "slow" twice clicks
          this.mainSketchVM?.complete();
          const length = (event.graphic.geometry as Polyline).paths[0].length;
          super.deleteVertex({ index: length - 1 });
          event.graphic.symbol = alignmentSymbol.designLineSymbol;
          this._isValidLine = true;
        }
      } else if (event.toolEventInfo.type === 'cursor-update') {
        if (!this._isValidLine) {
          this.mainSketchVM?.undo();
          this.clearInvalidState(event.graphic, 1500);
        }
      }
    }

    if (event.state === 'complete') {
      designStore.disableInput = false;
      // designStore.autoProfileZoom = true;
      this._mapTooltipProvider?.stop();

      if (!this._isValidLine) {
        const length = (event.graphic.geometry as Polyline).paths[0].length;
        super.deleteVertex({ index: length - 1 });
        this.clearInvalidState(this.sketchLayer.graphics.getItemAt(0), 1500);
      }

      if (!projectsLibraryStore.resetZValue) {
        await this.generateAndDisplayAlignmentProfile((event.graphic.geometry as Polyline).paths[0], true);
        this.broadcastSketchState({ state: SketchState.COMPLETE, keyCode: this.profileKeys[1] });
      } else {
        this.broadcastSketchState({ state: SketchState.COMPLETE, keyCode: this.profileKeys[1] });
        this.hub.ProfileStore?.reset(1);
      }
    }

    if (event.state === 'cancel') {
      designStore.disableInput = false;
      // designStore.autoProfileZoom = true;
      this._mapTooltipProvider?.stop();
      this.broadcastSketchState({ state: SketchState.CANCEL, keyCode: this.profileKeys[0] });
    }
  }

  generateAlignmentLabelsLayer(): void {
    // remove old label layer if it exists
    this.clearAlignmentLabelsLayer();

    const autoLabelLyr = this.mapView?.map.findLayerById(this.autoAlignmentLabelLayerId);
    if (autoLabelLyr) this.mapView?.map.remove(autoLabelLyr);

    if (this.mainSketchVM?.layer && this.mainSketchVM.layer.graphics && this.mainSketchVM.layer.graphics.length > 0) {
      const graphic = this.mainSketchVM.layer.graphics.getItemAt(0);
      const line = graphic.geometry as Polyline;
      if(!line) return;
      const path = line.paths[0];
      const alignmentStations = alignmentDesignStore.alignmentObject?.alignmentStations;
           
      const length = path.length;
      const pointSource = [] as Graphic[];
      const autoLabelSource = [] as Graphic[];

      for (let i = 0; i < length; i++) {
        const stationLabel = formatStationString(alignmentStations[i].stationName.toString());
        const point = new Graphic({
          geometry: new Point({
            spatialReference: line.spatialReference,
            x: path[i][0],
            y: path[i][1],
          }),
          attributes: {
            ObjectID: i,
            Station: stationLabel
          },
        });
        pointSource.push(point);
        if (i === 0 && this.autoGenerated && graphic.attributes.description) {
          point.attributes.Description = graphic.attributes.description;
          autoLabelSource.push(point);
        }
      }

      pointLabelClass.minScale = this.mapView!.scale * 0.9;
      const alingmenStations = new FeatureLayer({
        source: pointSource,
        fields: pointLabelFields,
        renderer: this.AlignmentStationPointRenderer as any,
        id: 'AlignmentStationLabels',
        title: 'Alignment Station Labels',
        objectIdField: 'ObjectID',
        displayField: 'Station',
        labelingInfo: [pointLabelClass]
      });

      if(this.mapView && this.mapView.map) {
        const groupLayer = this.mapView.map.findLayerById(`alignment${alignmentDesignStore.alignmentObject?.alignmentId}`) as GroupLayer;
        if (groupLayer) {
          groupLayer.add(alingmenStations);
        }
      }

      if (this.autoGenerated && graphic.attributes.description) {
        const autoLabel = new FeatureLayer({
          source: autoLabelSource,
          fields: autoLabelFields,
          renderer: this.AlignmentStationPointRenderer as any,
          id: this.autoAlignmentLabelLayerId,
          title: 'Auto Alignment Label',
          objectIdField: 'ObjectID',
          displayField: 'Description',
          labelingInfo: [autoLabelClass]
        });
  
        this.mapView?.map.add(autoLabel);
      }
    }
  }

  clearAlignmentLabelsLayer() {
    if (this.mapView && this.mapView.map) {
      const groupLayer = this.mapView.map.findLayerById(`alignment${alignmentDesignStore.alignmentObject?.alignmentId}`) as GroupLayer;
      if (groupLayer) {
        const reachStationLayer = groupLayer.findLayerById('AlignmentStationLabels') as FeatureLayer;
        if (reachStationLayer) {
          const removedLayer = groupLayer.remove(reachStationLayer);
          if (removedLayer) removedLayer.destroy();
        }
      }
    }
  }

  async generateAndDisplayAlignmentProfile(data: any[], generateLabels: boolean = false, projectPoints: boolean = true) {
    try {
      this.startLengthyProfileProcess(true);
      await doEvents();
      const [profilePoints, beginningStation] = await this.generateAlignment(data, projectPoints);
      if (profilePoints.length > 1) {
        this.hub.ProfileStore?.drawOneTimeGroundProfile(profilePoints, false, beginningStation)
      }

      if (generateLabels) this.generateAlignmentLabelsLayer();
    } catch (error) {
      if ((error as any).response && (error as any).response.data && (error as any).response.data.errorMessage) {
        MessageCenter.addError((error as any).response.data.errorMessage);
      } else {
        MessageCenter.addError((error as any).message);
      }
    } finally {
      this.startLengthyProfileProcess(false);
    }
  }

  async generateAlignment(points, projectPoints: boolean = true) {
    const alignmentLine: {}[] = [];
    for (const point of points) {
      let dataPoint;
      if (projectPoints) {
        const mapPoint = new Point({ x: point[0], y: point[1], spatialReference: this.mapView!.spatialReference });
        dataPoint = esriProjection.project(mapPoint as Geometry, this.dataSpatialReference!) as Point;
      } else {
        dataPoint = point;
      }
      const obj = {
        cutOrFill: 0,
        description: "string",
        id: 0,
        keyString: "string",
        objectName: "string",
        pointName: "string",
        tinnable: true,
        visiable: true,
        x: dataPoint.x,
        y: dataPoint.y,
        z: 0
      }
      alignmentLine.push(obj);
    }

    const payload = {
      crossSectionLine: alignmentLine,
      sampleInterval: 3,
      sessionUuid: designStore.projectsStore.currentSessionId,
      beginningStationDis:Number(alignmentDesignStore.begginingStation)
    }
  const response = await StructureProfileController.alignmentCrossSectionUsingPOST(payload);

    const stationDTOList = response.data?.stationDTOList;
    const alignmentStations = alignmentDesignStore.alignmentObject?.alignmentStations;

    if (stationDTOList && alignmentStations) {
      for (const stationDTO of stationDTOList) {
        const inputPoint = stationDTO.inputPoint;

        if (inputPoint?.description === "CrossVertex") {
          const alignmentStation = alignmentStations.find(station => 
            Number(station.x).toFixed(1) === Number(inputPoint.x).toFixed(1)
          );         
           if (alignmentStation) {
            alignmentStation.stationDistance = Number(inputPoint.pointName) ?? alignmentStation.stationDistance;
          }
        }
      }
      designStore.alignmentProfileStations = stationDTOList;
    }
    const profilePoints: any[] = [];
    let beginningStation;
    if (response.data && response.data.stationDTOList) {
      beginningStation = response.data.stationDTOList[0].distance;
      for (const station of response.data.stationDTOList) {
        if (station.inputPoint && station.inputPoint.x && station.inputPoint.y && station.inputPoint.z) {
          const point = new XyzPoint(station.inputPoint.x, station.inputPoint.y, station.inputPoint.z);
          profilePoints.push(point);
        }
      }
    }

    return [profilePoints, beginningStation];
  }

  async redrawAlignmentProfile() {
    const lyr = this.mapView?.map.findLayerById('alignment_sketch') as any;
    const pointsPath = lyr.graphics.items[0].geometry.paths[0];
    await this.generateAndDisplayAlignmentProfile(pointsPath);
  }

  async handlePointCreateEvent(event: __esri.SketchViewModelCreateEvent): Promise<void> {
    if (event.state === 'complete' && event.graphic && event.graphic.geometry) {
      const insertPoint = event.graphic.geometry as Point;
      const sr = event.graphic.geometry.spatialReference;

      if (this.sketchLayer.graphics && this.sketchLayer.graphics.length > 1) {
        const alignment = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
        const path = alignment?.paths[0];

        const [startPoint, stopPoint] = this.getStartAndStopPoint(alignment);

        if (esriGeometryEngine.distance(insertPoint, startPoint) === 0) {
          this.extendSketchVM?.create('point');
          this.insertPointStatus = 'startPoint';
          this.rubberbandSketchVm?.create(insertPoint);
        } else if (esriGeometryEngine.distance(insertPoint, stopPoint) === 0) {
          this.extendSketchVM?.create('point');
          this.insertPointStatus = 'stopPoint';
          this.rubberbandSketchVm?.create(insertPoint);
        } else if (this.isSnappedOnVertex(insertPoint, alignment)) {
          this.insertPointStatus = 'regular';
        } else {
          this.insertPointStatus = 'regular';

          if (path) {
            let pointIndex = -1;
            let beginPoint = path[0];
            for (let index = 1; index < path.length; index++) {
              const endPoint = path[index];
              const polyline = new Polyline({ paths: [[beginPoint, endPoint]], spatialReference: sr });
              if (esriGeometryEngine.intersects(polyline, insertPoint)) {
                pointIndex = index;
                break;
              }
              beginPoint = endPoint;
            }

            const dataPoint = esriProjection.project(insertPoint as Geometry, this.dataSpatialReference!);

            // prepare for profile view
            const dataX = (dataPoint as Point).x;
            const dataY = (dataPoint as Point).y;

            let dataZ = designStore.currentSurface.surfaceDefClassName === 'survey' ? ElevationInterpolater.Current?.Interpolate(dataX, dataY, insertPoint) : await getElevationUsingContours(dataPoint, insertPoint, this.mapView);
            if (dataZ === undefined) {
              dataZ = 0;
            }

            const newVertex: XyzPoint = new XyzPoint(dataX, dataY, dataZ);
            if (pointIndex >= 0) {
              alignment.insertPoint(0, pointIndex, insertPoint);
              this.broadcastInsertVertexInfo(newVertex, pointIndex, this.profileKeys[0], true);
              this.generateAlignmentLabelsLayer();
            }

            if (!projectsLibraryStore.resetZValue) {
              await this.generateAndDisplayAlignmentProfile(alignment.paths[0]);
            }

            this.curveLayer.graphics.removeAll();
            if (this.applyCurvesFunction) this.applyCurvesFunction();
          }
        }
      }

      if (this.mainSketchVM) this.mainSketchVM!.snappingOptions = {} as any;
      this.sketchLayer.graphics.pop();
    } else if (event.state === 'cancel') {
      if (this.mainSketchVM) this.mainSketchVM!.snappingOptions = {} as any;
    }
  }

  async handleReshapeEvent(event: __esri.SketchViewModelUpdateEvent): Promise<void> {
    if (event.toolEventInfo) {
      // console.log(event);
      if (event.toolEventInfo.type.includes('reshape-stop')) {
        const line = event.graphics[0].geometry as Polyline;
        if (this.isValidFromStart(line)) {
          this.clearInvalidState(event.graphics[0]);

          const toolEventInfo = event.toolEventInfo as __esri.MoveEventInfo;
          if (toolEventInfo) {
            const moverAttr = toolEventInfo.mover.attributes;
            const movedPoint: Point = toolEventInfo.mover.geometry as Point;
            const dataPoint = esriProjection.project(movedPoint as Geometry, this.dataSpatialReference!);

            //prepare for profile view
            const dataX = (dataPoint as Point).x;
            const dataY = (dataPoint as Point).y;

            let dataZ = designStore.currentSurface.surfaceDefClassName === 'survey' ? ElevationInterpolater.Current?.Interpolate(dataX, dataY, movedPoint) : await getElevationUsingContours(dataPoint, movedPoint, this.mapView);

            if (dataZ === undefined || dataZ === 0) {
              dataZ = isSurveyPoint(dataX, dataY);
            }

            const is3dAlignment = await this.checkFor3dAlignment(moverAttr.pointIndex);
            const bPolygon = getBoundaryPolygon(projectsLibraryStore.projectSpatialReference);
            let pointInsidePolygon = await isPointInsidePolygon(dataX, dataY, bPolygon, projectsLibraryStore.projectSpatialReference!);
            if (!pointInsidePolygon) {
              const value = isSurveyPoint(dataX, dataY);
              pointInsidePolygon = value === 0 ? false : true;
            }
            if (is3dAlignment && pointInsidePolygon) {
              projectsLibraryStore.resetZValue = false;
              await this.convertEdited2dTo3dAlignment(dataZ, moverAttr.pointIndex);
            } else if (!pointInsidePolygon) {
              MessageCenter.addWarningFromCode('Alignment_22');
              projectsLibraryStore.resetZValue = true;
            }
            const newVertex: XyzPoint = new XyzPoint(dataX, dataY, dataZ);
            if (moverAttr.pointIndex >= 0) {
              this.broadcastMoveVertexInfo(newVertex, moverAttr.pointIndex, this.profileKeys[0], true, this.alignmentObject);
              this.generateAlignmentLabelsLayer();
            }

            if (!projectsLibraryStore.resetZValue) {
              await this.generateAndDisplayAlignmentProfile((event.graphics[0].geometry as Polyline).paths[0]);
            } else {
              this.hub.ProfileStore?.reset(1);
            }

            this.curveLayer.graphics.removeAll();
            if (this.applyCurvesFunction) this.applyCurvesFunction();
          }
        } else {
          event.graphics[0].symbol = alignmentSymbol.invalidLineSymbol;
          await MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);

          // cleare error state
          setTimeout(() => {
            this.mainSketchVM?.undo();
            event.graphics[0].symbol = alignmentSymbol.designLineSymbol;
            this._isValidLine = true;
          }, 500);

          setTimeout(() => {
            // this.raiseError('');
            MessageCenter.clearMessages();
          }, 1500);
        }

        // console.log(moverAttr);
      } else if (event.toolEventInfo.type.includes('move-start')) {
      } else if (event.toolEventInfo.type.includes('move')) {
        this.mainSketchVM?.undo();
      } else if (event.toolEventInfo.type.includes('vertex-remove')) {
        this.mainSketchVM?.undo();
      } else if (event.toolEventInfo.type.includes('vertex-add')) {
        this.mainSketchVM?.undo();
        this.isAfterReshapeVertexAdd = true;
      } else if (event.toolEventInfo.type.includes('reshape-start')) {
      } else if (event.toolEventInfo.type.includes('reshape')) {
        if (this.isAfterReshapeVertexAdd) {
          this.mainSketchVM?.undo();
          this.isAfterReshapeVertexAdd = false;
        }
      }
    }
  }

  async checkToConvertAlignment(index, fromDeletion) {
    const is3dAlignment = await this.checkFor3dAlignment(index, fromDeletion);
    if (is3dAlignment) {
      projectsLibraryStore.resetZValue = false;
      await this.convertEdited2dTo3dAlignment();
    }
  }

  async convertEdited2dTo3dAlignment(dataZ?, index?) {
    const zeroZValues = this.alignmentObject.alignmentStations.some(station => station.z == 0);
    if (zeroZValues) {
      for (let i = 0; i < this.alignmentObject.alignmentStations.length; i++) {
        const station = this.alignmentObject.alignmentStations[i];
        if (i === index) {
          station.z = dataZ;
        }
        else {
          const stationX = Number(station.x);
          const stationY = Number(station.y);
          const stationDataPoint = new Point({ x: stationX, y: stationY, spatialReference: this.dataSpatialReference });
          const stationMapPoint = esriProjection.project(stationDataPoint, this.mapSpatialReference!) as Point;
          let z = designStore.currentSurface.surfaceDefClassName === 'survey' ? ElevationInterpolater.Current?.Interpolate(station.x, station.y, stationMapPoint) : await getElevationUsingContours(stationDataPoint, stationMapPoint, this.mapView);
          if (z === 0) {
            z = isSurveyPoint(stationX, stationY);
          }
          station.z = z;
        }
      }
    }
  }


  async checkFor3dAlignment(modifiedIndex, fromDeletion = false) {
    let is3dAlignment = true;
    if (!fromDeletion) {
      for (let i = 0; i < this.alignmentObject.alignmentStations.length; i++) {
        if (i !== modifiedIndex) {
          const station = this.alignmentObject.alignmentStations[i];
          const bPolygon = getBoundaryPolygon(projectsLibraryStore.projectSpatialReference);
          const pointInsidePolygon = await isPointInsidePolygon(station.x, station.y, bPolygon, projectsLibraryStore.projectSpatialReference!);
          if (!pointInsidePolygon) {
            // Check if point is border point
            const value = isSurveyPoint(Number(station.x), Number(station.y));
            is3dAlignment = value === 0 ? false : true;
          }
        }
      }
    } else {
      for (const station of this.alignmentObject.alignmentStations) {
        const bPolygon = getBoundaryPolygon(projectsLibraryStore.projectSpatialReference);
        const pointInsidePolygon = await isPointInsidePolygon(station.x, station.y, bPolygon, projectsLibraryStore.projectSpatialReference!);
        if (!pointInsidePolygon) {
          // Check if point is border point
          const value = isSurveyPoint(Number(station.x), Number(station.y));
          is3dAlignment = value === 0 ? false : true;
        }
      }
    }

    return is3dAlignment;
  }

  private editableStationPointRenderer = {
    type: 'simple',
    symbol: new SimpleMarkerSymbol({
      color: '#FF7F00',
      size: 7.5,
      style: 'circle',
      outline: {
        width: 1.25,
        color: '#7E4E1E'
      }
    }),
  };

  deleteStationFromMapClick(deleteFunction) {
    if (!this.isDeleteActive) {
      this.isDeleteActive = true;
      this.mainSketchVM?.complete();
      if (this.mapView && this.mainSketchVM?.layer && this.mainSketchVM.layer.graphics && this.mainSketchVM.layer.graphics.length > 0) {
        const line = this.mainSketchVM.layer.graphics.getItemAt(0).geometry as Polyline;
        const origPath = [...line.paths[0]];
        origPath.shift();
        origPath.pop();
        if (origPath.length > 0) {
          const path = origPath;
          const length = path.length;
          const pointSource = [] as Graphic[];

          for (let i = 0; i < length; i++) {
            const point = new Graphic({
              geometry: new Point({
                spatialReference: line.spatialReference,
                x: path[i][0],
                y: path[i][1],
              }),
              attributes: {
                ObjectID: i,
              },
            });
            pointSource.push(point);
          }

          const pointsLayer = new FeatureLayer({
            source: pointSource,
            renderer: this.editableStationPointRenderer as any,
            id: 'deleteMapStations',
            objectIdField: 'ObjectID',
          });

          this.mapView.map.add(pointsLayer);
          this.mapView.highlightOptions = {
            haloOpacity: 1,
            fillOpacity: 1,
          };

          let highlight: any;

          try {
            const options: __esri.MapViewHitTestOptions = {};
            options.include = pointsLayer;

            const highlightGraphics = async (e: any): Promise<void> => {
              if (highlight) {
                highlight.remove();
                highlight = undefined;
                return;
              }

              const result = await this.mapView?.hitTest(e, options);
              if (result?.results[0]) {
                const graphic = result.results[0].graphic;

                if (highlight) {
                  highlight.remove();
                  highlight = null;
                  return;
                }

                if (highlight) {
                  return;
                }

                if (this.mapView) {
                  for (const layerView of this.mapView.allLayerViews) {
                    if (layerView.layer === graphic.layer) {
                      highlight = (layerView as GraphicsLayerView).highlight(graphic);
                    }
                  }
                }
              } else {
                // remove the highlight if no features are returned from the hitTest
                if (highlight) {
                  highlight.remove();
                  highlight = null;
                }
              }
            };

            const getGraphics = async (response: any): Promise<void> => {
              const result = await this.mapView?.hitTest(response, options);
              if (result?.results[0]) {
                const graphic = result.results[0].graphic;

                const attributes = graphic.attributes;
                const id = attributes.ObjectID + 1;

                setTimeout(() => {
                  const stationName = this.alignmentObject.alignmentStations[id].stationName;
                  deleteFunction(stationName);
                  this.mapView?.map.remove(pointsLayer);
                  this.clearDeleteStation();
                }, 100);
              } else {
                return;
              }
            }

            this.mapImmediateClickHandle = this.mapView.on('immediate-click', getGraphics);
            this.mapPointerMoveHandle = this.mapView.on('pointer-move', highlightGraphics);


          } finally {
            if (highlight) {
              highlight.remove();
              highlight = undefined;
            }
          }
        }
      }
    } else {
      this.clearDeleteStation();
    }
  }

  clearDeleteStation() {
    this.mainSketchVM?.cancel();
    this.isDeleteActive = false;
    if (this.mapImmediateClickHandle) {
      this.mapImmediateClickHandle.remove();
      this.mapImmediateClickHandle = undefined;
    }
    if (this.mapPointerMoveHandle) {
      this.mapPointerMoveHandle.remove();
      this.mapPointerMoveHandle = undefined;
    }
    if (this.mapView) {
      this.mapView.highlightOptions = {
        haloOpacity: 0,
        fillOpacity: 0,
      }
      const layer = this.mapView.map.findLayerById('deleteMapStations');
      const Labellayer = this.mapView.map.findLayerById('AlignmentStationLabels');
      const autoLayer = this.mapView.map.findLayerById(this.autoAlignmentLabelLayerId);
      if (layer) this.mapView.map.remove(layer);
      if (Labellayer) this.mapView.map.remove(Labellayer);
      if (autoLayer) this.mapView.map.remove(autoLayer);
    }
  }

  updateAlignmentOnMap(data: any, vertexIndex) {
    this.alignmentObject = data;
    this.reset();
    this.setStore.isSketchMode = false;
    const alignment = this.alignmentObject.alignmentStations;
    if (alignment) {
      const paths = [
        alignment.map((r) => {
          return [r.x, r.y, r.z];
        }),
      ];
      const dataAlignmentLine = new Polyline({
        spatialReference: this.dataSpatialReference,
        paths,
      });
      const mapAlignmentLine = esriProjection.project(dataAlignmentLine, this.mapSpatialReference!);
      const graphic = new Graphic({
        geometry: mapAlignmentLine as Polyline,
        symbol: alignmentSymbol.designLineSymbol,
      });
      this.sketchLayer.add(graphic);
      this.broadcastTableStationEdit(data, graphic, vertexIndex);
    }
  }

  async broadcastTableStationEdit(data, graphic, vertexIndex) {
    const line = graphic.geometry as Polyline;
    if (this.isValidFromStart(line)) {
      this.clearInvalidState(graphic);

      const movedStation = this.alignmentObject.alignmentStations[vertexIndex];
      const dataPoint = new Point({ x: movedStation.x, y: movedStation.y, spatialReference: this.dataSpatialReference });
      const mapPoint = esriProjection.project(dataPoint as Geometry, this.mapSpatialReference!);

      //prepare for profile view
      const dataX = (dataPoint).x;
      const dataY = (dataPoint).y;

      const dataZ = designStore.currentSurface.surfaceDefClassName === 'survey' ? ElevationInterpolater.Current?.Interpolate(dataX, dataY, mapPoint as Point) : await getElevationUsingContours(dataPoint, mapPoint, this.mapView);

      const is3dAlignment = await this.checkFor3dAlignment(vertexIndex);
      const bPolygon = getBoundaryPolygon(projectsLibraryStore.projectSpatialReference);
      let pointInsidePolygon = await isPointInsidePolygon(dataX, dataY, bPolygon, projectsLibraryStore.projectSpatialReference!);
      if (!pointInsidePolygon) {
        const value = isSurveyPoint(dataX, dataY);
        pointInsidePolygon = value === 0 ? false : true;
      }
      if (is3dAlignment && pointInsidePolygon) {
        projectsLibraryStore.resetZValue = false;
        await this.convertEdited2dTo3dAlignment(dataZ, vertexIndex);
      }
      if (!pointInsidePolygon) {
        MessageCenter.addWarningFromCode('Alignment_22');
        projectsLibraryStore.resetZValue = true;
      }
      const blockModifyProfile = (projectsLibraryStore.resetZValue || designStore.currentSurface.surfaceDefClassName === 'dem') ? true : false;

      const newVertex: XyzPoint = new XyzPoint(dataX, dataY, dataZ);
      if (vertexIndex >= 0) {
        this.broadcastMoveVertexInfo(newVertex, vertexIndex, this.profileKeys[0], blockModifyProfile, this.alignmentObject);
      }

      if (designStore.currentSurface.surfaceDefClassName === 'dem') {
        if (!projectsLibraryStore.resetZValue) {
          try {
            this.startLengthyProfileProcess(true);
            await doEvents();
            this.userActivePoints = [];
            this.alignmentObject.alignmentStations.forEach((station) => {
              const newVertex: XyzPoint = new XyzPoint(Number(station.x), Number(station.y), Number(station.z));
              this.userActivePoints.push(newVertex);
            })
            this.getContourIntersects(false);
            // *********************************************************************
            // FOR TERRACE IMPLEMENTATION - Need to update the terraceDesignStore.alignmentProfileStations with the edit, which uses the [{distance, inputPoint: {elevation}}] format instead of XYZ format.
            this.hub.ProfileStore?.drawOneTimeGroundProfile(this.activeProfilePoints, false, Number(alignmentDesignStore.begginingStation));
            // *********************************************************************
          } finally {
            this.startLengthyProfileProcess(false);
            this.userActivePoints = [];
          }
        }
      }
      designStore.originalStation = {};
    } else {
      graphic.symbol = alignmentSymbol.invalidLineSymbol;
      await MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);
      // clear error state
      setTimeout(() => {
        this._isValidLine = true;
      }, 500);

      setTimeout(() => {
        MessageCenter.clearMessages();
        data.alignmentStations[vertexIndex] = designStore.originalStation;
        this.updateAlignmentOnMap(data, vertexIndex);
      }, 1500);
    }
  }

  @action
  protected async deleteVertex(info: any) {   
    if (this.sketchLayer.graphics && this.sketchLayer.graphics.length > 0) {
      // save "pre-delete" line here using 
      const originalPaths = JSON.parse(JSON.stringify((this.sketchLayer.graphics.getItemAt(0).geometry as Polyline).paths));
      
      await super.deleteVertex(info);
      const currentLine = this.sketchLayer.graphics.getItemAt(0).geometry as Polyline;
      if (this.isValidFromStart(currentLine)) {
        info.alignmentObject.alignmentStations.splice(info.index, 1);
        if (info.alignmentObject.alignmentXY) {
          info.alignmentObject.alignmentXY.splice(info.index, 1);
          await updateCurveAfterPointDeleted(info.tableStore, info.alignmentObject, info.fromTerrace);
        }
        await info.tableStore.checkToConvertAlignment(info.index, true);
                
        // Update profile
        if (this.hub) this.hub.ProfileVertexIndexToDeleteInfo = { index: info.index, keyCode: info.keyCode, alignmentObject: info.alignmentObject, blockModifyProfile: info.blockModifyProfile };

        this.clearInvalidState(this.sketchLayer.graphics.getItemAt(0));
        // last and first station bring to 0
        info.alignmentObject.alignmentStations[0].curveValue = 0;
        info.alignmentObject.alignmentStations[0].curveType = 0;
        const lastStationIndex = info.alignmentObject.alignmentStations.length - 1;
        info.alignmentObject.alignmentStations[lastStationIndex].curveValue = 0;
        info.alignmentObject.alignmentStations[lastStationIndex].curveType = 0;
        // redraw original and curved alignment
        await DisplayAlignment(
          gisModel.map,
          info.alignmentObject,
          info.alignmentObject.alignmentName,
          `alignment${info.alignmentObject.designElementId}`,
          '',
          appConfig.tempHardCodes.wkid,
          true
        );
        alignmentDesignStore.alignmentStationToDelete.stationName = '';
        if (!projectsLibraryStore.resetZValue) await this.redrawAlignmentProfile();
        if (info.alignmentObject.alignmentApplyCurves === 'true') await updateCurvesHandler(this.hub.TableStore, info.alignmentObject, info.fromTerrace);
        this.generateAlignmentLabelsLayer();
      } else {
        this.sketchLayer.graphics.getItemAt(0).symbol = alignmentSymbol.invalidLineSymbol;
        await MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);
      
        const originalLine = new Polyline({
          paths: originalPaths,
          spatialReference: currentLine.spatialReference,
        });
        setTimeout(() => {
          this.sketchLayer.graphics.getItemAt(0).geometry = originalLine;
          this.clearInvalidState(this.sketchLayer.graphics.getItemAt(0));
        }, 2000);
      }
      alignmentDesignStore.disableSaveAlignment = false;
    }
  }

  /** supporting functions */
  private _checkMapViewExtent(x: number, y: number, radius = 5000): void {
    if (this.mapView) {
      if (radius * 1.9 < Math.min(this.mapView.extent.height, this.mapView.extent.width)) {
        const newExtent = new Extent({
          xmin: x - 0.5 * radius,
          xmax: x + 1.5 * radius,
          ymin: y - radius,
          ymax: y + radius,
          spatialReference: this.mapSpatialReference,
        });
        this.mapView.extent = newExtent;
      }
    }
  }

  private isValidFromStart(line: __esri.Polyline) {
    let isValid = true;

    const spatialReference = line.spatialReference;
    const path = line.paths[0];
    for (let index = 2; index < path.length; index++) {
      const pathZero: number[][] = [];
      for (let j = 0; j <= index; j++) {
        pathZero.push(path[j]);
      }
      const checkedSegments = new Polyline({ paths: [pathZero], spatialReference });
      if (!this.isValidLastSegment(checkedSegments)) {
        isValid = false;
        this._isValidLine = false;
        break;
      }
    }
    return isValid;
  }

  private isValidLastSegment(line: Polyline): boolean {
    let isValid = true;

    const path = line.paths[0];
    const length = path.length;

    // check loops
    if (path.length >= 2) {
      const checkerPoint = line.getPoint(0, length - 1);
      for (let index = 0; index < length - 2; index++) {
        const checkeePoint = line.getPoint(0, index);
        if (esriGeometryEngine.distance(checkerPoint, checkeePoint) < this._loopThreshold) {
          // console.log('Alignment is not valid as it forms closed loop');
          isValid = false;
          this._isValidLine = false;
          break;
        }
      }
    }

    // check self-crossing
    if (isValid && path.length >= 3) {
      const spatialReference = line.spatialReference;
      const lastSegment = new Polyline({ paths: [[path[length - 2], path[length - 1]]], spatialReference });

      for (let index = 0; index < length - 3; index++) {
        const pntArr1 = path[index];
        const pntArr2 = path[index + 1];
        const currentSegment = new Polyline({ paths: [[pntArr1, pntArr2]], spatialReference });
        if (esriGeometryEngine.crosses(currentSegment, lastSegment)) {
          // console.log('OVERLAPS Alignment is not valid as it forms closed loop');
          isValid = false;
          this._isValidLine = false;
          break;
        }
      }
    }

    return isValid;
  }

  private clearInvalidState(graphic: Graphic | undefined, delay = 0) {
    this._isValidLine = true;
    if (graphic) graphic.symbol = alignmentSymbol.designLineSymbol;

    setTimeout(() => {
      // this.raiseError('');
      MessageCenter.clearMessages();
    }, delay);
  }

  private getStartAndStopPoint(polyline: Polyline): [Point, Point] {
    const startPoint = polyline.getPoint(0, 0);
    const lastIndex = polyline.paths[0].length - 1;
    const stopPoint = polyline.getPoint(0, lastIndex);
    return [startPoint, stopPoint];
  }

  private isSnappedOnVertex(point: Point, polyline: Polyline): boolean {
    let retVal = false;
    for (let index = 1; index < polyline.paths[0].length - 1; index++) {
      const vertex = polyline.getPoint(0, index);
      if (esriGeometryEngine.distance(point, vertex) === 0) {
        retVal = true;
        break;
      }
    }
    return retVal;
  }

  private _curveControlLayer: GraphicsLayer | undefined = void 0;
  protected get curveControlLayer() {
    const id = 'alignment_curve_control';
    const title = 'Curve Control Alignment';

    if (!this._curveControlLayer) {
      const layer = this.mapView?.map.findLayerById(id);
      if (layer) {
        this._curveControlLayer = layer as GraphicsLayer;
      } else {
        this._curveControlLayer = new GraphicsLayer({ id, title });
        this.mapView?.map.add(this._curveControlLayer, 3);
      }
    }

    return this._curveControlLayer;
  }

  protected removeCurveControlLayer() {
    if (this._curveControlLayer) {
      this.mapView?.map?.remove(this._curveControlLayer);
      this._curveControlLayer.destroy();
      this._curveControlLayer = void 0;
    }
  }

  /** clean-up and dispose */
  removeMapLayer(layer: DynamicProfileLayerType) {
    switch (layer) {
      case 'curved alignment':
        super.removeCurve();
        this.removeCurveControlLayer();
        break;

      case 'primary alignment':
        super.reomvoePrimary();
        this.removeCurveControlLayer();
        break;

      default:
        break;
    }
  }

  removeAlignmentGraphic() {
    super.removeSketchGraphic();
  }

  // dummy implementation  of abstract methods
  addStations() { }
  clearReachStations() {}

  cleanUp() {
    super.cleanUp();

    this.removeCurveControlLayer();

    if (this._extendSketchLayer) {
      this.mapView?.map?.remove(this._extendSketchLayer);
      this._extendSketchLayer.destroy();
      this._extendSketchLayer = void 0;
    }
  }

  Dispose() {
    // console.log('Alignment map store disposed.');

    this.cleanUp();
    super.Dispose();

    this.extendSketchVM?.destroy();

    this.__curveCircleIHandle?.remove();
  }

  private get mapTrackLayer(): GraphicsLayer | undefined {
    if (this.mapView) {
      const id = 'editing_track_lyr';
      const title = 'editing track';
  
      if (!this._mapTrackLayer) {
        const lyr = this.mapView.map.findLayerById(id);
        if (lyr) {
          this._mapTrackLayer = lyr as GraphicsLayer;
        } else {
          this._mapTrackLayer = new GraphicsLayer({ id, title, listMode: 'hide' });
          this.mapView.map.add(this._mapTrackLayer);
        }
      }
    }
    return this._mapTrackLayer;
  }

  public trackMapPoint(point: Point, id: string) {
    if (!this.mapTrackLayer?.graphics.toArray().some(graphic => {
      return (graphic.attributes && graphic.attributes.id && (graphic.attributes.id === id));
    })) {
      const graphic = new Graphic({
        geometry: point,
        symbol: reachHighlightSymbol,
        attributes: { id: id }
      });
      this.mapTrackLayer?.graphics.push(graphic);
    }
  }

  public unTrackMapPoint(id: string) {
    const graphicToRemove = this.mapTrackLayer?.graphics.find(graphic => {
      return (graphic.attributes && graphic.attributes.id && (graphic.attributes.id === id));
    });
    if (graphicToRemove) {
      this.mapTrackLayer?.graphics.remove(graphicToRemove);
    }
  }

  clearAlignment() {
    this._lookupDistance = [];
    this._lookupXY = [];
    this.clearTracker();
  }

  async setAlignment(data: any) { 
    this.clearAlignment();

    if (!this.dataSpatialReference?.equals(this.mapSpatialReference!)) {
      if (!esriProjection.isLoaded()) {
        await esriProjection.load();
      }
    }

    this._lookupDistance = data.map(point => { return point.distance; });
    const points = data.map(point => {
      return [
        point.inputPoint.x,
        point.inputPoint.y,
        convertLinearUnit(point.inputPoint.z, 'intl-feet', 'meters')
      ];
    });
    const dataLine = new Polyline({
      spatialReference: this.dataSpatialReference,
      hasZ: true,
      paths: [points]
    });
    const line = (
      this.dataSpatialReference?.equals(this.mapSpatialReference!) ?
      dataLine :
      esriProjection.project(dataLine, this.mapSpatialReference!) as Polyline
    );
    this._lookupXY = line.paths[0].map(point => { return [point[0], point[1]]; });

    // Set up map move handle
    const hitLayer = this.createPointArrayLayer(alignmentDesignStore.alignmentObject.alignmentStations, 'alignment_hit_layer', 0);
    const options: __esri.MapViewHitTestOptions = {};
    options.include = hitLayer;

    this.viewPointerMoveHandle1?.remove();
    this.viewPointerMoveHandle1 = this.mapView?.on('pointer-move', async (e: __esri.ViewPointerMoveEvent) => {
      const result = await this.mapView?.hitTest(e, options);
        if (result?.results[0]) {
          const index: number | undefined = result?.results[0].graphic.attributes['ObjectID'];
          if ((index !== undefined) && (index > -1) && (index < alignmentDesignStore.alignmentObject.alignmentStations.length)) {
            const station = alignmentDesignStore.alignmentObject.alignmentStations[index];
            if (this.hub.ProfileStore) {
              (this.hub.ProfileStore as DynamicAlignmentProfileStore).trackStation(station, designStore.commonHorizontalStretchFactor, designStore.commonVerticalStretchFactor);
            }
            if (this.hub.TableStore) {
              (this.hub.TableStore as DynamicAlignmentTableStore).highlightRows(index);
            }
            this.trackStation(station);
          }
        } else {
          if (this.hub.ProfileStore) {
            (this.hub.ProfileStore as DynamicAlignmentProfileStore).untrackStations();
          }
          if (this.hub.TableStore) {
            (this.hub.TableStore as DynamicAlignmentTableStore).clearHighlights();
          }
          this.untrackAllStations();
        }
    });
  }

  private createPointArrayLayer(array: any[], id: string, opacity: number): FeatureLayer {
    const pointSource = [] as Graphic[];

    array.forEach((station, index) => {
      const point = new Point({
        x: station.x,
        y: station.y,
        spatialReference: this.dataSpatialReference
      });
      const projectedPoint = esriProjection.project(point, this.mapSpatialReference!) as Point;
      const geom = new Graphic({
        geometry: projectedPoint,
        attributes: {
          ObjectID: index
        }
      })
      pointSource.push(geom)
    });

    const layerToDelete = this.mapView?.map.findLayerById(id);
    if (layerToDelete) this.mapView?.map.remove(layerToDelete);

    const newLayer = new FeatureLayer({
      source: pointSource,
      id: id,
      title: id,
      opacity: opacity,
      listMode: 'hide',
      objectIdField: 'ObjectID'
    });
    this.mapView?.map.add(newLayer);

    return newLayer
  } 

  trackStation(station) {
    this.untrackAllStations();

    const mapPoint = new Point({
      x: station.x,
      y: station.y,
      z: station.z,
      spatialReference: this.dataSpatialReference
    })
    this.trackMapPoint(mapPoint, `id_${station.stationName}`);
  }

  untrackAllStations() {
    this.mapTrackLayer?.graphics.removeAll();
  }

  setTracker(d: number, z: number) {
    if (this._lookupDistance.length > 0 && this._lookupXY.length > 0) {
      const coordXY = this.interpolateCoords(d, this._lookupDistance, this._lookupXY);
      const point = new Point({
        spatialReference: this.mapSpatialReference,
        hasZ: true,
        x: coordXY[0],
        y: coordXY[1],
        z: convertLinearUnit(z, 'intl-feet', 'meters')
      })
  
      if (this._trackerGraphic) {
        this._trackerGraphic.geometry = point;
      } else {
        this._trackerGraphic = new Graphic({ geometry: point, symbol: trackerSymbol });
        this.sketchLayer.add(this._trackerGraphic);
      }
    }
  }

  clearTracker() {
    if (this._trackerGraphic) {
      this.sketchLayer.graphics.remove(this._trackerGraphic);
      this._trackerGraphic = undefined;
    }
    if (this.hub.ProfileStore) {
      (this.hub.ProfileStore as DynamicAlignmentProfileStore).untrackStations();
    }
    if (this.hub.TableStore) {
      (this.hub.TableStore as DynamicAlignmentTableStore).clearHighlights();
    }
    this.untrackAllStations();
  }

  displayPreviewPoints(points) {
    this.createPointArrayLayer(points, this._pointsPreviewLayerId, 100);
  }

  clearPreviewPoints() {
    const layerToDelete = this.mapView?.map.findLayerById(this._pointsPreviewLayerId);
    if (layerToDelete) this.mapView?.map.remove(layerToDelete);
  }

  async autoGenerateAlignment(points: any[]) {
    if (this.mainSketchVM) {
      this.mainSketchVM.layer.graphics.removeAll();
      
      let valid = true;
      const mapLine = new Polyline({paths: [[]], spatialReference: this.mapSpatialReference});
      
      const sortedPoints = points.every(p => /\d+$/.test(p.pointName)) ? points.sort((a, b) => {
        const numA = parseInt(a.pointName.match(/\d+$/)[0], 10);
        const numB = parseInt(b.pointName.match(/\d+$/)[0], 10);
        return numA - numB
      }) : points;
      for(const point of sortedPoints) {
        const dataPoint = new Point({x: point.x, y: point.y, spatialReference: this.dataSpatialReference});
        const mapPoint = esriProjection.project(dataPoint, this.mapSpatialReference!) as Point;
        mapLine.paths[0].push([mapPoint.x, mapPoint.y]);
        if (!this.isValidLastSegment(mapLine)) valid = false;
      }

      const graphic = new Graphic({
        geometry: mapLine,
        symbol: alignmentSymbol.designLineSymbol,
        attributes: {
          description: `${points[0].featureDescription}`
        }
      });
      this.mainSketchVM.layer.graphics.add(graphic);

      if (valid) {
        this.autoGenerated = true;
        designStore.disableInput = false;
        sortedPoints.forEach((point, index) => {
          const newVertex: XyzPoint = new XyzPoint(point.x, point.y, point.z);
          const status = index === 0 ? 'start' : 'active';
          (this.hub.TableStore as DynamicAlignmentTableStore).addRow(newVertex, status, index);
        });
        await this.generateAndDisplayAlignmentProfile(sortedPoints, true, false);
      } else {
        this.mainSketchVM.layer.graphics.getItemAt(0).symbol = alignmentSymbol.invalidLineSymbol;
        await MessageCenter.addErrorFromCode(ERROR_SELF_CROSSING);
        setTimeout(() => {
          this.mainSketchVM?.layer.graphics.removeAll();
        }, 2000)
      }
    }
  } 
}

export { DynamicAlignmentMapStore };
